import { createRoute, Outlet, redirect } from "@tanstack/react-router";
import { useResizable } from "react-resizable-layout";
import { z } from "zod";
import { useLocalSettingsStore } from "@/common/stores";
import { RedirectWhenSignedOut } from "@/components/auth/RedirectWhenSignedOut";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import NewListDialog from "@/components/NewListDialog";
import { OrganizationSwitchOverlay } from "@/components/OrganizationSwitchOverlay";
import { PaidConversionTracker } from "@/components/PaidConversionTracker";
import { trpc } from "@/components/QueryProvider";
import SettingsDialog from "@/components/settings/SettingsDialog";
import { AppSidebar } from "@/components/sidebar/AppSidebar";
import { TimerRealtimeBridge } from "@/components/TimerRealtimeBridge";
import { TrialExpiredDialog } from "@/components/TrialExpiredDialog";
import { SidebarProvider } from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { VerticalResizeSeparator } from "@/components/VerticalResizeSeparator";
import { FeaturesProvider } from "@/contexts/FeaturesContext";
import { flushPendingSignUp } from "@/lib/analytics";
import { authClient } from "@/lib/auth";
import { debounce } from "@/lib/debounce";
import { SocketProvider } from "@/lib/socket/SocketContext";
import { trpcClient } from "@/lib/trpc";
import { sessionQueryOptions } from "@/queries/queries";
import { createTeamRoute } from "./createTeamRoute";
import { joinTeamRoute } from "./joinTeamRoute";
import { loginRoute } from "./loginRoute";
import { rootRoute } from "./rootRoute";

const appSearchSchema = z.object({
    settings: z.string().optional(),
});

export const appRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: "app", // this is the id of the route instead of a path
    validateSearch: appSearchSchema,
    loader: async ({ context: { queryClient } }) => {
        const opts = trpc.taskStatus.list.queryOptions(undefined, {
            staleTime: Infinity, // Never becomes stale, need explicit refetch
            gcTime: Infinity, // Keep in cache forever
        });
        const features = trpc.features.list.queryOptions(undefined, {
            staleTime: Infinity, // Never becomes stale, need explicit refetch
            gcTime: Infinity, // Keep in cache forever
        });
        queryClient.ensureQueryData(features);
        queryClient.ensureQueryData(opts);

        // prefetch the session data so we don't fetch it in parallel multiple times
        queryClient.prefetchQuery(sessionQueryOptions());

        // Awaited so the first render already uses the user's date/time preferences
        // instead of flashing the defaults. prefetchQuery never throws: on failure the
        // defaults apply (see useUserPreferences).
        await queryClient.prefetchQuery(trpc.user.preferences.queryOptions(undefined, { staleTime: Infinity }));
    },
    beforeLoad: async () => {
        const session = await authClient.getSession();
        if (!session?.data) {
            throw redirect({
                to: loginRoute.to,
                search: {
                    redirect: window.location.href,
                },
            });
        }

        flushPendingSignUp(session.data.user);

        const memberships = await trpcClient.user.memberships.query();
        if (memberships.length === 0) {
            const pendingInvitations = await trpcClient.auth.getPendingInvitations.query();
            if (pendingInvitations.length > 0) {
                throw redirect({
                    to: joinTeamRoute.to,
                });
            }
            throw redirect({
                to: createTeamRoute.to,
            });
        }

        return {
            user: session.data?.user,
        };
    },
    component: () => {
        const { sidebarWidth, setSidebarWidth } = useLocalSettingsStore();
        const debouncedSaveSidebarWidth = debounce((width: number) => {
            setSidebarWidth(width);
        }, 300);
        const { position, separatorProps } = useResizable({
            axis: "x",
            min: 200,
            max: 700,
            initial: sidebarWidth,
            onResizeStart: () => {
                document.body.classList.add("horizontal-resizing");
            },
            onResizeEnd: (size) => {
                document.body.classList.remove("horizontal-resizing");
                debouncedSaveSidebarWidth(size.position);
            },
        });

        return (
            <div className="h-screen flex bg-orsane-background">
                <RedirectWhenSignedOut />
                <ImpersonationBanner />
                <FeaturesProvider>
                    <SocketProvider>
                        {/* The one subscriber for timer:changed; see the component. */}
                        <TimerRealtimeBridge />
                        <SidebarProvider>
                            <div style={{ width: position }}>
                                <AppSidebar />
                            </div>
                            <VerticalResizeSeparator {...separatorProps} />
                            <main className="h-full flex-1 relative bg-orsane-background p-2 pl-0">
                                <TooltipProvider>
                                    <Outlet />
                                </TooltipProvider>
                            </main>
                        </SidebarProvider>
                    </SocketProvider>
                </FeaturesProvider>

                <SettingsDialog />
                <NewListDialog />
                <TrialExpiredDialog />
                <PaidConversionTracker />
                <OrganizationSwitchOverlay />
                {/* <TanStackRouterDevtools position="bottom-right" />
                <ReactQueryDevtools /> */}
                {/* <ReactQueryDevtools /> */}
            </div>
        );
    },
});
